/**
 * Tenant-scoped query-key factory (D-10). Every key MUST start with
 * `[tenantSlug, ...]` or `["_platform", ...]`. ESLint (FOUND-03) bans raw
 * `useQuery` / `useMutation` outside @linkhr/api-client — this factory is
 * the single way app code produces keys.
 *
 * Shape: `[tenantSlug, entity, op, params?]` — enables prefix-invalidation:
 *
 *   queryClient.invalidateQueries({ queryKey: qk.employees.all(slug) });
 *   queryClient.removeQueries({ queryKey: qk.tenant(oldSlug) }); // logout flush
 *
 * Examples:
 *   qk.employees.list("acme", { branchId: "b1" })
 *     → ["acme", "employees", "list", { branchId: "b1" }]
 *   qk.platform.tenants.list({})
 *     → ["_platform", "tenants", "list", {}]
 */

/** Branded tenant-slug type. Prevents accidentally passing a raw string. */
export type TenantSlug = string & { readonly __brand: "TenantSlug" };

/**
 * Runtime validation + brand helper. Call once at tenant resolution, then
 * pass the branded value through.
 *
 * Slug rules (mirror backend validation):
 *   - Lowercase ASCII letter first char
 *   - 1..63 chars total
 *   - Letters / digits / hyphens only
 *   - "_platform" is reserved (not a slug — the qk factory uses it for platform-aud)
 */
export function asTenantSlug(s: string): TenantSlug {
  if (!/^[a-z][a-z0-9-]{0,62}$/.test(s)) {
    throw new Error(`Invalid tenant slug: ${JSON.stringify(s)}`);
  }
  if (s === "_platform") {
    throw new Error(`Reserved slug: "_platform" (used by qk.platform.*)`);
  }
  return s as TenantSlug;
}

/** Tenant cache-root prefix — consumed by `queryClient.removeQueries` on logout. */
const tenant = (slug: TenantSlug | string) => [slug] as const;

export const qk = {
  tenant,
  auth: {
    session: (slug: TenantSlug | string) => [slug, "auth", "session"] as const,
    branding: (slug: TenantSlug | string) => [slug, "auth", "branding"] as const,
  },
  assistant: {
    all: (slug: TenantSlug | string) => [slug, "assistant"] as const,
    conversations: {
      list: (slug: TenantSlug | string, filters: object = {}) =>
        [slug, "assistant", "conversations", "list", filters] as const,
      detail: (slug: TenantSlug | string, id: string) =>
        [slug, "assistant", "conversations", "detail", id] as const,
    },
    runs: {
      detail: (slug: TenantSlug | string, runId: string) =>
        [slug, "assistant", "runs", "detail", runId] as const,
    },
    /** A conversation's durable history, per assistant surface. */
    transcript: (slug: TenantSlug | string, surface: string, conversationId: string) =>
      [slug, "assistant", surface, "transcript", conversationId] as const,
    turns: {
      /** The turn-submit mutation, per assistant surface. */
      create: (slug: TenantSlug | string, surface: string) =>
        [slug, "assistant", surface, "turns", "create"] as const,
    },
  },
  me: {
    /**
     * Phase 2 — per-user preferences (CC-04: theme/density/locale).
     * Not tenant-scoped — a user's prefs travel with them across tenant-scoped
     * nav. On logout, the whole QueryClient cache is cleared at the app layer.
     */
    preferences: () => ["me", "preferences"] as const,
    /**
     * Phase 3 — employee record of the caller (CC-07 `GET /api/v1/me`).
     * User-scoped (no tenant slug) — the JWT identifies the tenant server-side
     * via the `tid` claim; the response body is the caller's own employee row.
     */
    employee: () => ["me", "employee"] as const,
    /**
     * Phase 3 — self-service documents list (CC-07 `GET /api/v1/me/documents`).
     * User-scoped, consumed by Employee Web Documents tab + renewal-alert banner.
     */
    documents: () => ["me", "documents"] as const,
    /**
     * Self-service refresh-token session list (`GET /api/v1/me/sessions`).
     * User-scoped (no tenant slug) so the cache survives a tenant flip.
     */
    sessions: () => ["me", "sessions"] as const,
    /**
     * An AI app's request to connect to the caller's account
     * (`GET /api/v1/oauth/authorization-requests/{id}`), read by the consent page.
     */
    connectorRequest: (id: string) => ["me", "connector-request", id] as const,
    /**
     * A change an AI app prepared, waiting for the caller's approval in Link HR
     * (`GET /api/v1/ai-actions/{id}`). The server binds it to the caller.
     */
    aiAction: (id: string) => ["me", "ai-action", id] as const,
    /**
     * The caller's saved dashboard layout (`GET /api/v1/me/dashboard-layout`).
     * Starts with the `"me"` sentinel so logout flushes it; the slug keeps one
     * tenant's layout apart from another's for the same person.
     */
    dashboardLayout: (slug: TenantSlug | string) => ["me", "dashboardLayout", slug] as const,
    /**
     * Phase 6.1 — self-service leave balance (CC-34
     * `GET /api/v1/employees/me/leave-balance`). User-scoped — JWT carries
     * the tenant; cache entry travels with the user across slug flips.
     */
    leaveBalance: () => ["me", "leaves", "balance"] as const,
    /**
     * Phase 6.1 — self-service leave ledger (CC-34
     * `GET /api/v1/employees/me/leave-ledger`). User-scoped; consumers
     * append their filter params to the key for per-filter cache entries.
     */
    leaveLedger: () => ["me", "leaves", "ledger"] as const,
    /**
     * Phase 05.2 Plan 13 — self-service payslip history (`GET /api/v1/me/payslips`).
     * User-scoped (no tenant slug; JWT carries `tid` server-side). Pagination
     * params live in the tail so per-page cache entries stay distinct, mirroring
     * `qk.employees.directory` from Plan 11.
     */
    payslips: (query: { page?: number; size?: number } = {}) => ["me", "payslips", query] as const,
    /**
     * Phase 05.2 Plan 13 — per-payslip presigned download URL
     * (`POST /api/v1/me/payslips/{lineId}:presign-download`). Mutation-style
     * surface; we still expose a query-key namespace so onSettled invalidation
     * + per-line cache entries stay first-class for retries.
     */
    payslipPresign: (lineId: string) => ["me", "payslips", "presign", lineId] as const,
    /**
     * CC-79 (be-086) — caller's YTD payroll aggregate
     * (`GET /api/v1/me/payslips/ytd?year={year}`). User-scoped; year=current
     * by default. PAID-only per backend semantics.
     */
    payslipYtd: (year: number | undefined) => ["me", "payslips", "ytd", year ?? "current"] as const,
    /**
     * CC-134 (be-190) — caller's current monthly compensation snapshot
     * (`GET /api/v1/me/compensation`). User-scoped; refreshes on tenant
     * switch via the standard `useTenantQuery` invalidation.
     */
    compensation: () => ["me", "compensation"] as const,
    /**
     * CC-285 — self-service vacation-in-advance payout + recovery for one leave
     * request (`GET /me/compensation/vacation-in-advance/{leaveRequestId}`).
     */
    vacationInAdvance: (leaveRequestId: string) =>
      ["me", "vacationInAdvance", leaveRequestId] as const,
    /**
     * The employee's own view of wages paid ahead of a leave
     * (`GET /me/compensation/leave-prepay/{leaveRequestId}`).
     */
    leavePrepay: (leaveRequestId: string) => ["me", "leave-prepay", leaveRequestId] as const,
    /** Self-service EOS preview (`GET /me/eos-preview`, CC-227). */
    eosPreview: () => ["me", "eos-preview"] as const,
    /** Self-service emergency contacts (`GET/PATCH /employees/me/emergency-contacts`). */
    emergencyContacts: () => ["me", "emergency-contacts"] as const,
    /**
     * Caller's own bank-account change requests (`GET /me/bank-account-change-requests`).
     * Financial data: tenant-prefixed so a tenant switch never serves another
     * tenant's cached rows.
     */
    bankAccountChanges: (slug: TenantSlug | string) =>
      [slug, "me", "bank-account-change-requests"] as const,
    /** One of them (`GET /me/bank-account-change-requests/{id}`), under the list's prefix. */
    bankAccountChange: (slug: TenantSlug | string, id: string) =>
      [slug, "me", "bank-account-change-requests", id] as const,
    /** Caller's own ACTIVE bank accounts, masked (`GET /employees/me/bank-accounts`). */
    bankAccounts: (slug: TenantSlug | string) => [slug, "me", "bank-accounts"] as const,
    /**
     * Company documents published to the caller (`GET /me/company-documents/{id}`).
     * User-scoped; `detail` carries the id so per-document reads stay distinct.
     */
    companyDocuments: {
      all: () => ["me", "company-documents"] as const,
      list: (query: Record<string, unknown> = {}) =>
        ["me", "company-documents", "list", query] as const,
      detail: (id: string) => ["me", "company-documents", "detail", id] as const,
    },
    /**
     * Employee Profile Depth — self sub-resources (`/employees/me/*`).
     * User-scoped (JWT carries the tenant); every replaceMy* mutation
     * invalidates its own list key.
     */
    profile: {
      education: () => ["me", "profile", "education"] as const,
      skills: () => ["me", "profile", "skills"] as const,
      languages: () => ["me", "profile", "languages"] as const,
      certifications: () => ["me", "profile", "certifications"] as const,
      identityDocuments: () => ["me", "profile", "identity-documents"] as const,
      medical: () => ["me", "profile", "medical-info"] as const,
    },
  },
  employees: {
    all: (slug: TenantSlug | string) => [slug, "employees"] as const,
    list: (slug: TenantSlug | string, query: Record<string, unknown>) =>
      [slug, "employees", "list", query] as const,
    /** Every page of `GET /employees` for one filter bag (`useEmployeeListAll`). */
    listAll: (slug: TenantSlug | string, filters: Record<string, unknown>) =>
      [slug, "employees", "list-all", filters] as const,
    detail: (slug: TenantSlug | string, id: string) => [slug, "employees", "detail", id] as const,
    /** Prefix-matches `all`, so the terminate/reverse mutations already clear it. */
    termination: (slug: TenantSlug | string, employeeId: string) =>
      [slug, "employees", "termination", employeeId] as const,
    /**
     * Per-employee current salary structure + history
     * (`GET /api/v1/employees/{id}/salary` + `/salary-history`).
     */
    salary: (slug: TenantSlug | string, employeeId: string) =>
      [slug, "employees", "salary", employeeId] as const,
    /**
     * Per-employee bank-account collection (`GET|PUT /api/v1/employees/{id}/bank-accounts`).
     * One key for the whole set: the PUT writes the returned set + ETag straight
     * into this cache entry, so a save never round-trips.
     */
    bankAccounts: (slug: TenantSlug | string, employeeId: string) =>
      [slug, "employees", "bank-accounts", employeeId] as const,
    salaryHistory: (slug: TenantSlug | string, employeeId: string) =>
      [slug, "employees", "salary", "history", employeeId] as const,
    /** CC-299 — tenant-wide base-salary aggregate (`GET /api/v1/salary/aggregate`). */
    salaryAggregate: (slug: TenantSlug | string) =>
      [slug, "employees", "salary", "aggregate"] as const,
    /**
     * Per-employee lifecycle timeline (`GET /api/v1/employees/{id}/timeline`).
     * Cursor-paginated HR events (HIRED/SALARY_CHANGED/…). `query` carries the
     * `from`/`to` filter bag so distinct filters keep distinct cache entries.
     */
    timeline: (
      slug: TenantSlug | string,
      employeeId: string,
      query: Record<string, unknown> = {},
    ) => [slug, "employees", "timeline", employeeId, query] as const,
    /**
     * Phase 3 — per-employee documents (CC-07 `/api/v1/employees/{id}/documents`).
     * `list` is the invalidation handle after upload/delete mutations.
     */
    documents: {
      all: (slug: TenantSlug | string, employeeId: string) =>
        [slug, "employees", "documents", employeeId] as const,
      list: (slug: TenantSlug | string, employeeId: string) =>
        [slug, "employees", "documents", "list", employeeId] as const,
    },
    /**
     * The cross-employee document register (`GET /api/v1/employees/documents`).
     * The query params fold into the key so filtered reads never collide.
     */
    documentsRegister: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "employees", "documents-register", query] as const,
    /**
     * Employee Profile Depth (CC-81/82/83) — per-employee sub-resource
     * collections. Each `all(slug, employeeId)` is the surgical
     * invalidation root a mutation targets; `list` is the read key. Medical
     * is a 1:1 singleton (no list vs all split). Tenant-scoped per FOUND-03.
     */
    profile: {
      education: {
        all: (slug: TenantSlug | string, employeeId: string) =>
          [slug, "employees", "profile", "education", employeeId] as const,
        list: (slug: TenantSlug | string, employeeId: string) =>
          [slug, "employees", "profile", "education", employeeId, "list"] as const,
      },
      skills: {
        all: (slug: TenantSlug | string, employeeId: string) =>
          [slug, "employees", "profile", "skills", employeeId] as const,
        list: (slug: TenantSlug | string, employeeId: string) =>
          [slug, "employees", "profile", "skills", employeeId, "list"] as const,
      },
      languages: {
        all: (slug: TenantSlug | string, employeeId: string) =>
          [slug, "employees", "profile", "languages", employeeId] as const,
        list: (slug: TenantSlug | string, employeeId: string) =>
          [slug, "employees", "profile", "languages", employeeId, "list"] as const,
      },
      certifications: {
        all: (slug: TenantSlug | string, employeeId: string) =>
          [slug, "employees", "profile", "certifications", employeeId] as const,
        list: (slug: TenantSlug | string, employeeId: string) =>
          [slug, "employees", "profile", "certifications", employeeId, "list"] as const,
      },
      emergencyContacts: {
        all: (slug: TenantSlug | string, employeeId: string) =>
          [slug, "employees", "profile", "emergency-contacts", employeeId] as const,
        list: (slug: TenantSlug | string, employeeId: string) =>
          [slug, "employees", "profile", "emergency-contacts", employeeId, "list"] as const,
      },
      identityDocuments: {
        all: (slug: TenantSlug | string, employeeId: string) =>
          [slug, "employees", "profile", "identity-documents", employeeId] as const,
        list: (slug: TenantSlug | string, employeeId: string) =>
          [slug, "employees", "profile", "identity-documents", employeeId, "list"] as const,
      },
      medical: (slug: TenantSlug | string, employeeId: string) =>
        [slug, "employees", "profile", "medical-info", employeeId] as const,
    },
    /**
     * CC-79 (be-086) — HR-side per-employee YTD payroll aggregate
     * (`GET /api/v1/employees/{id}/payslips/ytd?year={year}`). PAID-only per
     * backend semantics; tenant-scoped via slug; year=current by default.
     */
    payslipsYtd: (slug: TenantSlug | string, employeeId: string, year: number | undefined) =>
      [slug, "employees", "payslips", "ytd", employeeId, year ?? "current"] as const,
    /**
     * CC-116 (be-169) — HR-side per-employee payslip history list
     * (`GET /api/v1/employees/{id}/payslips`). Permission `payroll:read` (HR)
     * or `payroll:read:team` (manager ancestor); manager rejected outright if
     * compVisibility=NONE. Default year = ALL; size capped at 100 server-side.
     */
    payslipsHistory: (
      slug: TenantSlug | string,
      employeeId: string,
      params: {
        year?: number | undefined;
        status?: string | undefined;
        page?: number | undefined;
        size?: number | undefined;
      },
    ) => [slug, "employees", "payslips", "history", employeeId, params] as const,
    /**
     * Phase 3 — bulk-import job polling (CC-07 `GET /employees/bulk-import/jobs/{jobId}`).
     * Consumed by the 4-step wizard's progress screen (D-11); refetchInterval
     * short-circuits on status=COMPLETED|FAILED.
     */
    /** CC-445 adaptive import-session poll (assistant composer attachment). */
    importSession: (slug: TenantSlug | string, sessionId: string) =>
      [slug, "employees", "import-session", sessionId] as const,
    bulkImport: {
      job: (slug: TenantSlug | string, jobId: string) =>
        [slug, "employees", "bulk-import", "job", jobId] as const,
      rows: (slug: TenantSlug | string, jobId: string) =>
        [slug, "employees", "bulk-import", "rows", jobId] as const,
    },
    /**
     * Phase 05.2 Plan 11 — coworker directory (EW-19).
     *
     * `GET /api/v1/employees/directory` returns `PagedDirectoryCards` (CC-61
     * adds `status` field; today the server filters `WHERE status='ACTIVE'`
     * so all rows return `"ACTIVE"`, but the field name is now contractual
     * and future relaxation surfaces without an API break).
     *
     * Tenant-scoped per FOUND-03 (key starts with `[slug, ...]`). Query
     * params (q/page/size/dept/location) live in the tail so per-filter
     * cache entries stay distinct.
     */
    teamProfile: (slug: TenantSlug | string, employeeId: string) =>
      [slug, "employees", "team-profile", employeeId] as const,
    directory: (
      slug: TenantSlug | string,
      query: { q?: string; page?: number; size?: number; dept?: string; location?: string },
    ) => [slug, "employees", "directory", query] as const,
    /** All directory pages folded into one list (name-resolution use). */
    directoryAll: (slug: TenantSlug | string) => [slug, "employees", "directory", "all"] as const,
    /** Whole-tenant reporting hierarchy, unpaged (`GET /employees/org-chart`). */
    orgChart: (slug: TenantSlug | string) => [slug, "employees", "org-chart"] as const,
    /**
     * Per-employee nursing periods (`/api/v1/employees/{id}/nursing-periods`).
     * `all` is the invalidation root a create/patch/cancel targets; `list` is
     * the read key. A single period is addressed under `detail` off its own id
     * (the PATCH/cancel routes are keyed by period id, not employee id).
     */
    nursingPeriods: {
      all: (slug: TenantSlug | string, employeeId: string) =>
        [slug, "employees", "nursing-periods", employeeId] as const,
      list: (slug: TenantSlug | string, employeeId: string) =>
        [slug, "employees", "nursing-periods", "list", employeeId] as const,
      detail: (slug: TenantSlug | string, periodId: string) =>
        [slug, "nursing-periods", "detail", periodId] as const,
    },
  },
  /**
   * Phase 3 — generic org config CRUD (D-15). Five resources share one
   * screen driven by a resource registry: levels, positions, contract-types,
   * branches, functions. The `resource` discriminator is the second segment
   * so `qk.org.resource(slug, "branches")` is a prefix of every
   * branches-scoped list query — invalidation stays surgical.
   */
  org: {
    resource: (slug: TenantSlug | string, resource: string) => [slug, "org", resource] as const,
    list: (slug: TenantSlug | string, resource: string, query: Record<string, unknown>) =>
      [slug, "org", resource, "list", query] as const,
  },
  /**
   * Phase 3 — holidays calendar (EMP-14). Endpoint `/api/v1/tenants/me/holidays`
   * is ABSENT from backend openapi.json as of 2026-04-19 (fe-008 filed) — keyed
   * by year because the Jordan preset + Hijri-floating resolver produces
   * per-year results.
   */
  holidays: {
    list: (slug: TenantSlug | string, year: number) => [slug, "holidays", "list", year] as const,
  },
  /**
   * Integrations → ZenHR migration (CC-496). One prefix so a connect, a run
   * start or a rescan invalidates connections, runs and coverage together —
   * they move as a unit and a stale coverage table next to a fresh run is the
   * confusing state to avoid.
   */
  integrations: {
    all: (slug: TenantSlug | string) => [slug, "integrations"] as const,
    /** The company's AI connector settings (`/tenants/me/integrations/ai-connectors`). */
    aiConnectors: (slug: TenantSlug | string) => [slug, "integrations", "ai-connectors"] as const,
    setup: (slug: TenantSlug | string) => [slug, "integrations", "zenhr", "setup"] as const,
    connections: (slug: TenantSlug | string) =>
      [slug, "integrations", "zenhr", "connections"] as const,
    runs: (slug: TenantSlug | string) => [slug, "integrations", "zenhr", "runs"] as const,
    coverage: (slug: TenantSlug | string, runId: string) =>
      [slug, "integrations", "zenhr", "coverage", runId] as const,
    progress: (slug: TenantSlug | string, runId: string) =>
      [slug, "integrations", "zenhr", "progress", runId] as const,
    cutover: (slug: TenantSlug | string, runId: string) =>
      [slug, "integrations", "zenhr", "cutover", runId] as const,
    cutoverDelta: (
      slug: TenantSlug | string,
      runId: string,
      page: number,
      materialOnly: boolean,
      size: number,
    ) => [slug, "integrations", "zenhr", "cutover-delta", runId, page, materialOnly, size] as const,
    balances: (slug: TenantSlug | string, runId: string, query: Record<string, unknown>) =>
      [slug, "integrations", "zenhr", "balances", runId, query] as const,
    figure: (slug: TenantSlug | string, runId: string, employee: string, sourceTypeId: string) =>
      [slug, "integrations", "zenhr", "figure", runId, employee, sourceTypeId] as const,
    batch: (slug: TenantSlug | string, runId: string, idempotencyKey: string) =>
      [slug, "integrations", "zenhr", "approve-batch", runId, idempotencyKey] as const,
    operations: (slug: TenantSlug | string, runId: string) =>
      [slug, "integrations", "zenhr", "operations", runId] as const,
    reconciliation: (slug: TenantSlug | string, runId: string) =>
      [slug, "integrations", "zenhr", "reconciliation", runId] as const,
    importStatus: (slug: TenantSlug | string, runId: string) =>
      [slug, "integrations", "zenhr", "import-status", runId] as const,
    leaveTypes: (slug: TenantSlug | string, runId: string) =>
      [slug, "integrations", "zenhr", "leave-types", runId] as const,
    sourceRows: (
      slug: TenantSlug | string,
      runId: string,
      sourceTypeId: string,
      page: number,
      size: number,
    ) => [slug, "integrations", "zenhr", "rows", runId, sourceTypeId, page, size] as const,
  },
  /**
   * Phase 3 — tenant role assignments (EMP-13). Endpoint
   * `/api/v1/tenant/roles/assignments` — query shape TBD (pagination / filter
   * on role) per DataTable+Drawer composition.
   */
  roles: {
    assignments: (slug: TenantSlug | string, query: Record<string, unknown>) =>
      [slug, "roles", "assignments", query] as const,
    /** Phase 08.1 Plan 06 — tenant role list (5 sibling cards). */
    list: (slug: TenantSlug | string) => [slug, "roles", "list"] as const,
    /** Phase 2 (CC-236) — capability + raw-permission catalog. */
    capabilities: (slug: TenantSlug | string) => [slug, "roles", "capabilities"] as const,
    /** Phase 2 (CC-236) — starter role templates. */
    templates: (slug: TenantSlug | string) => [slug, "roles", "templates"] as const,
    /** Phase 2 (CC-237) — roles summary table. */
    summary: (slug: TenantSlug | string) => [slug, "roles", "summary"] as const,
    /** Phase 2 (CC-237) — a role's editor round-trip state. */
    authoring: (slug: TenantSlug | string, roleId: string) =>
      [slug, "roles", "authoring", roleId] as const,
    /** A role's assigned users (expandable member list). */
    members: (slug: TenantSlug | string, roleId: string) =>
      [slug, "roles", "members", roleId] as const,
    /** The inverse listing — the roles one user holds (person-centric surface). */
    ofUser: (slug: TenantSlug | string, userId: string) =>
      [slug, "roles", "of-user", userId] as const,
    /** Everyone with access (the People hub) — under roles so role writes refresh it. */
    users: (slug: TenantSlug | string, query: Record<string, unknown>) =>
      [slug, "roles", "users", query] as const,
  },
  /**
   * Phase 3 — HR-admin branding PATCH surface (EMP-15, Brand Studio).
   * Distinct from `qk.auth.branding` which is the unauthenticated login
   * fetch — this key namespaces the authenticated HR-admin cache for
   * tenant-branding writes + revalidation.
   */
  branding: (slug: TenantSlug | string) => [slug, "branding"] as const,
  /**
   * Phase 3 — tenant-wide settings (EMP-16 EOS visibility toggle; future
   * general-settings expansions). Endpoint `/api/v1/tenants/me/settings`.
   */
  tenantSettings: (slug: TenantSlug | string) => [slug, "tenantSettings"] as const,
  /**
   * The tenant's legal jurisdiction — country, payroll currency, calendar
   * policy and the feature flags every country-dependent surface renders
   * from. Endpoint `GET /api/v1/tenants/me/jurisdiction`. Effectively
   * immutable for the life of a session, so it is cached for an hour.
   */
  jurisdiction: (slug: TenantSlug | string) => [slug, "jurisdiction"] as const,
  /**
   * The identity documents the tenant's jurisdiction issues, with bilingual
   * labels and client-side validation hints. Endpoint
   * `GET /api/v1/tenants/me/document-types`. Same lifetime as jurisdiction.
   */
  documentTypeCatalog: (slug: TenantSlug | string) => [slug, "documentTypeCatalog"] as const,
  /**
   * The banks this tenant's jurisdiction runs, with their IBAN codes and
   * bilingual names. Endpoint `GET /api/v1/tenants/me/banks`, ETag-backed and
   * versioned. Same lifetime as the document catalog — it changes when a bank
   * merges, which is not a within-session event.
   */
  bankCatalog: (slug: TenantSlug | string) => [slug, "bankCatalog"] as const,
  /**
   * The cities of the tenant's country (`GET /api/v1/tenants/me/cities`) —
   * branch cities and the answers to city questions. Same lifetime as the
   * bank directory.
   */
  cityCatalog: (slug: TenantSlug | string) => [slug, "cityCatalog"] as const,
  /**
   * Hiring (`/api/v1/recruiting/...`). Settings, stages and rejection reasons
   * share one server version, so a write to any of them invalidates
   * `settingsAll`. Every job write invalidates `jobs.all` (list, detail and
   * board together); every application write invalidates `applications.all`,
   * `jobs.all` and `candidates.all`, because job payloads carry the counts.
   */
  recruiting: {
    all: (slug: TenantSlug | string) => [slug, "recruiting"] as const,
    settingsAll: (slug: TenantSlug | string) => [slug, "recruiting", "settings"] as const,
    settings: (slug: TenantSlug | string) => [slug, "recruiting", "settings", "retention"] as const,
    stages: (slug: TenantSlug | string) => [slug, "recruiting", "settings", "stages"] as const,
    rejectionReasons: (slug: TenantSlug | string) =>
      [slug, "recruiting", "settings", "rejection-reasons"] as const,
    questionsAll: (slug: TenantSlug | string) => [slug, "recruiting", "questions"] as const,
    questions: (slug: TenantSlug | string, opts: { includeArchived?: boolean } = {}) =>
      [slug, "recruiting", "questions", opts] as const,
    jobs: {
      all: (slug: TenantSlug | string) => [slug, "recruiting", "jobs"] as const,
      list: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
        [slug, "recruiting", "jobs", "list", query] as const,
      detail: (slug: TenantSlug | string, jobId: string) =>
        [slug, "recruiting", "jobs", "detail", jobId] as const,
      board: (slug: TenantSlug | string, jobId: string, query: Record<string, unknown> = {}) =>
        [slug, "recruiting", "jobs", "board", jobId, query] as const,
      notice: (slug: TenantSlug | string, jobId: string) =>
        [slug, "recruiting", "jobs", "notice", jobId] as const,
    },
    applications: {
      all: (slug: TenantSlug | string) => [slug, "recruiting", "applications"] as const,
      search: (slug: TenantSlug | string, body: Record<string, unknown>) =>
        [slug, "recruiting", "applications", "search", body] as const,
      pages: (slug: TenantSlug | string, body: Record<string, unknown>, firstPage: number) =>
        [slug, "recruiting", "applications", "pages", body, firstPage] as const,
      detail: (slug: TenantSlug | string, applicationId: string) =>
        [slug, "recruiting", "applications", "detail", applicationId] as const,
      events: (slug: TenantSlug | string, applicationId: string) =>
        [slug, "recruiting", "applications", "events", applicationId] as const,
      hireDraft: (slug: TenantSlug | string, applicationId: string) =>
        [slug, "recruiting", "applications", "hire-draft", applicationId] as const,
    },
    /** A file's download link; outside `applications` so a write doesn't re-sign an open CV. */
    file: (slug: TenantSlug | string, applicationId: string, fileId: string) =>
      [slug, "recruiting", "file", applicationId, fileId] as const,
    candidates: {
      all: (slug: TenantSlug | string) => [slug, "recruiting", "candidates"] as const,
      detail: (slug: TenantSlug | string, candidateId: string) =>
        [slug, "recruiting", "candidates", "detail", candidateId] as const,
    },
  },
  /**
   * CC-282 — tenant Ramadan date ranges. Endpoint
   * `GET|PUT /api/v1/tenants/me/ramadan-periods`; PUT replace-all invalidates
   * this key on success.
   */
  ramadanPeriods: (slug: TenantSlug | string) => [slug, "ramadanPeriods"] as const,
  /**
   * fe-196 — tenant Setup feature (first-run wizard + dashboard checklist +
   * payroll gate). Single key namespace; `GET /api/v1/tenants/me/setup-status`
   * has no params. Every Setup mutation (`:review` / `:skip` / `:dismiss` /
   * `wizard:complete`) invalidates `status(slug)` on success.
   */
  tenantSetup: {
    status: (slug: TenantSlug | string) => [slug, "tenant-setup", "status"] as const,
  },
  /**
   * Tenant-scoped audit log (`GET /api/v1/audit-log`). Paginated; query carries
   * employeeId/actions/fromDate/toDate + Pageable. Permission `audit:read:tenant`.
   */
  auditLog: (slug: TenantSlug | string, query: Record<string, unknown>) =>
    [slug, "audit-log", query] as const,
  /**
   * Phase 21 / CC-122 (be-178) — tenant module toggles. Reads / writes the
   * effective plan + override merge from `/tenants/me/modules`. Cache
   * invalidates after a PATCH so sidebar visibility refreshes.
   */
  tenantModules: (slug: TenantSlug | string) => [slug, "tenantModules"] as const,
  /**
   * Phase 21 / CC-123 (be-178) — field-config matrix (tenant-scope). FE
   * consumes at session bootstrap to render hidden/required state on every
   * form. Admin-side writes invalidate this key.
   */
  fieldConfigMatrix: (slug: TenantSlug | string) => [slug, "fieldConfigMatrix"] as const,
  /**
   * Phase 21 / CC-123 — admin field-config catalogue (per-tenant full
   * row list, not the role-resolved matrix). Separate cache key so
   * /_admin reads don't clobber the bootstrap matrix consumed by forms.
   */
  fieldConfigAdmin: (slug: TenantSlug | string) => [slug, "fieldConfigAdmin"] as const,
  /**
   * Phase 21 / CC-127 (be-178) — tenant notification-event matrix.
   * Cross-product of `(eventKind, channel)` rows; PATCH/DELETE per cell.
   */
  notificationEvents: (slug: TenantSlug | string) => [slug, "notificationEvents"] as const,
  /**
   * Catch-all "Other" requests — employee-side (`/me/other-requests`) and
   * HR/manager-side (`/other-requests?scope=`). Two query-key namespaces so
   * the per-side list caches don't collide and invalidations stay surgical.
   *
   *   - `meOtherRequests.list`   — `GET /api/v1/me/other-requests`
   *   - `otherRequests.list`     — `GET /api/v1/other-requests?scope=`
   *   - `otherRequests.detail`   — `GET /api/v1/other-requests/{id}`
   *   - `otherRequests.all`      — prefix-invalidation handle for both list +
   *     detail caches after `:acknowledge` / `:resolve` writes.
   */
  meOtherRequests: {
    list: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "me-other-requests", "list", query] as const,
  },
  otherRequests: {
    all: (slug: TenantSlug | string) => [slug, "other-requests"] as const,
    list: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "other-requests", "list", query] as const,
    detail: (slug: TenantSlug | string, id: string) =>
      [slug, "other-requests", "detail", id] as const,
  },
  /**
   * Miscellaneous request types — the tenant-admin catalogue
   * (`/tenants/me/miscellaneous-types`) and the employee's requestable subset
   * (`/me/miscellaneous-types`). `all` is the prefix-invalidation handle after
   * create/update/archive writes.
   */
  miscTypes: {
    all: (slug: TenantSlug | string) => [slug, "misc-types"] as const,
    list: (slug: TenantSlug | string, status: string) =>
      [slug, "misc-types", "list", status] as const,
    mine: (slug: TenantSlug | string) => [slug, "misc-types", "mine"] as const,
  },
  /**
   * Suspension reasons — the tenant-admin catalogue
   * (`/tenants/me/suspension-reasons`). `all` is the prefix-invalidation
   * handle after create / full-replace / archive writes.
   */
  suspensionReasons: {
    all: (slug: TenantSlug | string) => [slug, "suspension-reasons"] as const,
    list: (slug: TenantSlug | string, status: string) =>
      [slug, "suspension-reasons", "list", status] as const,
  },
  /**
   * Employee suspensions — the tenant register (`/suspensions`), one record
   * (`/suspensions/{id}`), and the per-employee list
   * (`/employees/{employeeId}/suspensions`). `all` is the prefix-invalidation
   * handle after every record write; record mutations additionally invalidate
   * `qk.requests.all` (the acts ride the approvals feed) and the employee's
   * detail/list caches, which surface the in-effect suspension summary.
   */
  suspensions: {
    all: (slug: TenantSlug | string) => [slug, "suspensions"] as const,
    list: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "suspensions", "list", query] as const,
    detail: (slug: TenantSlug | string, id: string) => [slug, "suspensions", "detail", id] as const,
    forEmployee: (
      slug: TenantSlug | string,
      employeeId: string,
      query: Record<string, unknown> = {},
    ) => [slug, "suspensions", "employee", employeeId, query] as const,
    /** Manual-fire attachment download (`GET /suspensions/{id}/attachments/{attachmentId}:download`). */
    attachment: (slug: TenantSlug | string, id: string, attachmentId: string) =>
      [slug, "suspensions", "detail", id, "attachment", attachmentId] as const,
  },
  /**
   * Tasks — the scoped register (`/tasks?scope=`), one task (`/tasks/{id}`),
   * and the summary counts (`/tasks/summary`) on one side; the caller's own
   * list and detail (`/me/tasks`, `/me/tasks/{id}`) on the other. Two
   * namespaces, same split as other-requests, so the per-side caches don't
   * collide and invalidations stay surgical.
   *
   *   - `tasks.list`      — `GET /api/v1/tasks?scope=`
   *   - `tasks.detail`    — `GET /api/v1/tasks/{id}`
   *   - `tasks.summary`   — `GET /api/v1/tasks/summary`
   *   - `tasks.all`       — prefix-invalidation handle covering register
   *     lists, details and the summary.
   *   - `myTasks.list`    — `GET /api/v1/me/tasks`
   *   - `myTasks.detail`  — `GET /api/v1/me/tasks/{id}`
   *   - `myTasks.all`     — prefix-invalidation handle for the /me side.
   *
   * Every task write invalidates both namespaces: a completion or cancel
   * moves the row on whichever list shows it, and the summary counts change
   * with it.
   */
  tasks: {
    all: (slug: TenantSlug | string) => [slug, "tasks"] as const,
    list: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "tasks", "list", query] as const,
    detail: (slug: TenantSlug | string, id: string) => [slug, "tasks", "detail", id] as const,
    summary: (slug: TenantSlug | string) => [slug, "tasks", "summary"] as const,
  },
  myTasks: {
    all: (slug: TenantSlug | string) => [slug, "me-tasks"] as const,
    list: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "me-tasks", "list", query] as const,
    detail: (slug: TenantSlug | string, id: string) => [slug, "me-tasks", "detail", id] as const,
  },
  /**
   * HR letters — the admin surfaces and the employee's own side, split the
   * same way as tasks so per-side caches never collide.
   *
   *   - `letterCategories` — `GET /tenants/me/letter-categories?status=`
   *   - `letterTemplates`  — `GET /tenants/me/letter-templates` (paged) and one
   *     template by id
   *   - `letterVariables`  — `GET /tenants/me/letter-variables` (the palette)
   *   - `hrLetters`        — the issued register `GET /hr-letters` and one
   *     letter by id
   *   - `myHrLetters`      — `/me/hr-letters` list + detail, the templates the
   *     employee may use, and their letter requests
   *
   * Every letter write invalidates `hrLetters.all` and `myHrLetters.all`: the
   * same letter shows on both sides, and a request's approval issues a letter.
   */
  letterCategories: {
    all: (slug: TenantSlug | string) => [slug, "letter-categories"] as const,
    list: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "letter-categories", "list", query] as const,
  },
  letterTemplates: {
    all: (slug: TenantSlug | string) => [slug, "letter-templates"] as const,
    list: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "letter-templates", "list", query] as const,
    detail: (slug: TenantSlug | string, id: string) =>
      [slug, "letter-templates", "detail", id] as const,
  },
  /**
   * `POST :preview` read as a query for the template cards' first-page image.
   * Deliberately OUTSIDE `letterTemplates.all`: every template write (a star
   * included) invalidates that prefix, and a rendered page costs the backend
   * real work — it only changes when the template does, which `updatedAt`
   * carries.
   */
  letterTemplatePreview: (
    slug: TenantSlug | string,
    id: string,
    updatedAt: string,
    language: string,
    employeeId: string,
  ) => [slug, "letter-template-previews", id, updatedAt, language, employeeId] as const,
  letterVariables: (slug: TenantSlug | string) => [slug, "letter-variables"] as const,
  hrLetters: {
    all: (slug: TenantSlug | string) => [slug, "hr-letters"] as const,
    list: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "hr-letters", "list", query] as const,
    detail: (slug: TenantSlug | string, id: string) => [slug, "hr-letters", "detail", id] as const,
  },
  myHrLetters: {
    all: (slug: TenantSlug | string) => [slug, "me-hr-letters"] as const,
    list: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "me-hr-letters", "list", query] as const,
    detail: (slug: TenantSlug | string, id: string) =>
      [slug, "me-hr-letters", "detail", id] as const,
    templates: (slug: TenantSlug | string) => [slug, "me-hr-letters", "templates"] as const,
    requests: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "me-hr-letters", "requests", query] as const,
  },
  /**
   * Mass changes — the attribute catalogue, the register, one job, and a
   * job's rows. `all` is the prefix-invalidation handle after apply / resume.
   */
  massChanges: {
    all: (slug: TenantSlug | string) => [slug, "mass-changes"] as const,
    attributes: (slug: TenantSlug | string) => [slug, "mass-changes", "attributes"] as const,
    jobs: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "mass-changes", "jobs", query] as const,
    job: (slug: TenantSlug | string, id: string) => [slug, "mass-changes", "job", id] as const,
    rows: (slug: TenantSlug | string, id: string, query: Record<string, unknown> = {}) =>
      [slug, "mass-changes", "job", id, "rows", query] as const,
  },
  /**
   * Task-assignment catalogues — the reference data the assign-task drawer
   * reads when a kind names another record: the tenant's upload-document-type
   * catalogue (`GET /tenants/me/upload-document-types`) for DOCUMENT_UPLOAD
   * and the company-document register (`GET /company-documents`) for
   * DOCUMENT_DOWNLOAD. Read-only lists; the query params fold into the key so
   * a filtered read never serves another filter's rows.
   */
  taskCatalogs: {
    uploadDocumentTypes: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "upload-document-types", query] as const,
    companyDocuments: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "company-documents", query] as const,
  },
  /**
   * Upload document types — the tenant catalogue of document kinds
   * (`/tenants/me/upload-document-types`) plus the caller's own effective
   * list (`/employees/me/upload-document-types`). `all` is the
   * prefix-invalidation handle after create / full-replace / archive writes;
   * it also covers the taskCatalogs keys, which nest under the same prefix.
   */
  uploadDocumentTypes: {
    all: (slug: TenantSlug | string) => [slug, "upload-document-types"] as const,
    list: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "upload-document-types", "list", query] as const,
    mine: (slug: TenantSlug | string) => [slug, "upload-document-types", "mine"] as const,
  },
  /**
   * Company documents — the HR register (`/company-documents`), one document
   * by id, and its receipts. `all` is the prefix-invalidation handle after
   * create / patch / archive writes.
   */
  companyDocuments: {
    all: (slug: TenantSlug | string) => [slug, "company-documents"] as const,
    list: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "company-documents", "list", query] as const,
    detail: (slug: TenantSlug | string, id: string) =>
      [slug, "company-documents", "detail", id] as const,
    receipts: (slug: TenantSlug | string, id: string, query: Record<string, unknown> = {}) =>
      [slug, "company-documents", "receipts", id, query] as const,
  },
  /**
   * Salary packages — the per-employee effective-dated timeline
   * (`/api/v1/employees/{employeeId}/salary-packages`). Keyed per employee:
   * `all` is the prefix-invalidation handle after draft/activate writes —
   * activation also moves current pay, so mutations additionally invalidate
   * `qk.employees.salary*` alongside this block.
   */
  salaryPackages: {
    all: (slug: TenantSlug | string, employeeId: string) =>
      [slug, "employees", "salary-packages", employeeId] as const,
    list: (slug: TenantSlug | string, employeeId: string, query: Record<string, unknown> = {}) =>
      [slug, "employees", "salary-packages", employeeId, "list", query] as const,
    detail: (slug: TenantSlug | string, employeeId: string, id: string) =>
      [slug, "employees", "salary-packages", employeeId, "detail", id] as const,
    /** A package priced before it is saved, keyed on the body that prices it. */
    preview: (slug: TenantSlug | string, employeeId: string, body: unknown) =>
      [slug, "employees", "salary-packages", employeeId, "preview", body] as const,
    /**
     * The tenant's component catalogue, assembled from the package templates.
     * Tenant-wide, not per-employee: it keys off the slug alone.
     */
    componentCatalogue: (slug: TenantSlug | string) =>
      [slug, "payroll", "salary-components", "catalogue"] as const,
  },
  /**
   * CC-166 — tenant-side billing surface for HR-Admin Billing page
   * (`/settings/billing`). Mirrors the existing platform-side `qk.platform.*`
   * keys but scoped to the caller's own tenant via slug.
   *
   *   - `subscription`   — `GET /api/v1/tenants/me/subscription`
   *   - `invoices.list`  — `GET /api/v1/tenants/me/billing/invoices`
   *   - `invoices.detail`— `GET /api/v1/tenants/me/billing/invoices/{id}`
   *   - `contact`        — `GET /api/v1/tenants/me/billing/contact`
   *   - `deliveryPrefs`  — `GET /api/v1/tenants/me/billing/delivery-prefs`
   *   - `paymentMethod`  — `GET /api/v1/tenants/me/billing/payment-method`
   */
  myBilling: {
    root: (slug: TenantSlug | string) => [slug, "myBilling"] as const,
    subscription: (slug: TenantSlug | string) => [slug, "myBilling", "subscription"] as const,
    invoices: {
      all: (slug: TenantSlug | string) => [slug, "myBilling", "invoices"] as const,
      list: (slug: TenantSlug | string, query: Record<string, unknown>) =>
        [slug, "myBilling", "invoices", "list", query] as const,
      detail: (slug: TenantSlug | string, id: string) =>
        [slug, "myBilling", "invoices", "detail", id] as const,
    },
    contact: (slug: TenantSlug | string) => [slug, "myBilling", "contact"] as const,
    deliveryPrefs: (slug: TenantSlug | string) => [slug, "myBilling", "deliveryPrefs"] as const,
    paymentMethod: (slug: TenantSlug | string) => [slug, "myBilling", "paymentMethod"] as const,
  },
  /**
   * Phase 4 — leaves + requests (LEAVE-01..LEAVE-07 + EW-09..EW-12).
   *
   * Shape mirrors 04-RESEARCH §"State Strategy" + 04-CONTEXT §D-07 (balance-preview
   * debounced + `keepPreviousData`). `all`/`list`/`detail` are Phase 3 holdovers
   * that remain the coarse invalidation handles for the (deferred) unified
   * `GET /requests` LIST endpoint — new Phase 4 builders live alongside them:
   *   - `types` / `type`           — leave-types CRUD (LEAVE-01/02)
   *   - `calendar`                 — team calendar substitute for missing LIST (LEAVE-05, Pitfall H5)
   *   - `balancePreview`           — live debounced pre-submit impact (EW-10, D-07)
   *   - `balance` / `ledger`       — per-employee balance + audit trail (LEAVE-07, EW-09 — MSW-only per fe-021/be-023)
   */
  leaves: {
    all: (slug: TenantSlug | string) => [slug, "leaves"] as const,
    /** Company-scope leave-request page (`useLeaveRequestsPage`, the Time off page). */
    list: (slug: TenantSlug | string, query: Record<string, unknown>) =>
      [slug, "leaves", "list", query] as const,
    detail: (slug: TenantSlug | string, id: string) => [slug, "leaves", "detail", id] as const,
    /**
     * CC-300 — presigned attachment download for a leave request
     * (`GET /leave-requests/{id}/attachment`). Manual-fire (`enabled: false`),
     * keyed per leave-request id so concurrent views don't cross-talk.
     */
    attachment: (slug: TenantSlug | string, id: string) =>
      [slug, "leaves", "attachment", id] as const,
    types: (slug: TenantSlug | string) => [slug, "leaves", "types"] as const,
    type: (slug: TenantSlug | string, id: string) => [slug, "leaves", "types", id] as const,
    calendar: (slug: TenantSlug | string, from: string, to: string, scope: string) =>
      [slug, "leaves", "calendar", { from, to, scope }] as const,
    balancePreview: (
      slug: TenantSlug | string,
      params: { leaveTypeCode: string; from: string; to: string } | null,
    ) => [slug, "leaves", "balance-preview", params] as const,
    balance: (slug: TenantSlug | string, employeeId: string | "me") =>
      [slug, "leaves", "balance", employeeId] as const,
    ledger: (
      slug: TenantSlug | string,
      employeeId: string | "me",
      filters: Record<string, unknown>,
    ) => [slug, "leaves", "ledger", employeeId, filters] as const,
    /**
     * Company-wide ledger (`GET /leave-ledger`) — the Balance adjustments and
     * Carried-over pages. `companyLedger(slug, {})` partial-matches every
     * filtered variant, which is what an adjustment invalidates.
     */
    companyLedger: (slug: TenantSlug | string, filters: Record<string, unknown>) =>
      [slug, "leaves", "company-ledger", filters] as const,
  },
  /**
   * Phase 4 — unified requests hub (D-01). The backend unified
   * `GET /api/v1/requests` LIST is deferred to Phase 6.1 per be-025; until
   * then, the FE adapter composes `/leaves/calendar` + `/overtime-requests`
   * under this key so consumers don't change when the real endpoint ships.
   */
  requests: {
    /** Prefix for invalidating every request list/detail/unified variant. */
    all: (slug: TenantSlug | string) => [slug, "requests"] as const,
    list: (slug: TenantSlug | string, filters: Record<string, unknown>) =>
      [slug, "requests", "list", filters] as const,
    detail: (slug: TenantSlug | string, id: string) => [slug, "requests", "detail", id] as const,
    /** Phase 08.2 — unified endpoint `GET /api/v1/requests` (scope=team). */
    unifiedList: (slug: TenantSlug | string, params: Record<string, unknown>) =>
      [slug, "requests", "unified", params] as const,
    /**
     * `GET /api/v1/requests/{id}` — one request as the discriminated `RequestView`
     * union. Distinct from `detail` (the leave-shaped detail cache) so the two
     * shapes never collide on one cache entry.
     */
    unifiedDetail: (slug: TenantSlug | string, id: string) =>
      [slug, "requests", "unified-detail", id] as const,
  },
  /**
   * Phase 4 — overtime requests (LEAVE-06, EW-12). Separate surface from
   * `requests.*` because the backend LIST exists here (`GET /overtime-requests`)
   * where leaves LIST does not (be-011).
   */
  overtime: {
    list: (slug: TenantSlug | string, params: Record<string, unknown>) =>
      [slug, "overtime", "list", params] as const,
    detail: (slug: TenantSlug | string, id: string) => [slug, "overtime", "detail", id] as const,
    /** CC-329 — presigned attachment download. */
    attachment: (slug: TenantSlug | string, id: string) =>
      [slug, "overtime", "attachment", id] as const,
    /** Pay estimate for the caller — keyed on every input the endpoint reads. */
    preview: (slug: TenantSlug | string, date: string, hours: number) =>
      [slug, "overtime", "preview", date, hours] as const,
  },
  /**
   * Phase 5 — attendance day records, summaries, and adjustment history
   * (ATT-01..ATT-05, EW-06..EW-08). Keys include date/scope params so
   * per-scope cache entries stay isolated.
   */
  attendance: {
    all: (slug: TenantSlug | string) => [slug, "attendance"] as const,
    list: (slug: TenantSlug | string, query: Record<string, unknown>) =>
      [slug, "attendance", "list", query] as const,
    days: (slug: TenantSlug | string, params: Record<string, unknown>) =>
      [slug, "attendance", "days", params] as const,
    /**
     * Every company day row in [from, to], paged to the end (`useAttendanceDaysRange`),
     * or one person's with `employeeId`. Under the `days` prefix, so an adjust (which
     * invalidates it) refreshes the team views.
     */
    daysRange: (slug: TenantSlug | string, from: string, to: string, employeeId?: string) =>
      [slug, "attendance", "days", "range", { from, to, employeeId }] as const,
    summary: (slug: TenantSlug | string, employeeId: string, year: number, month: number) =>
      [slug, "attendance", "summary", employeeId, year, month] as const,
    history: (slug: TenantSlug | string, date: string) =>
      [slug, "attendance", "history", date] as const,
  },
  /**
   * Phase 5 — shift templates CRUD (ATT-01, CC-15).
   */
  shifts: {
    list: (slug: TenantSlug | string) => [slug, "shifts", "list"] as const,
    detail: (slug: TenantSlug | string, id: string) => [slug, "shifts", "detail", id] as const,
    /** The company assignment list (`GET /api/v1/shift-assignments`); every assignment write
     *  invalidates this prefix. */
    assignmentPages: (slug: TenantSlug | string) => [slug, "shift-assignments"] as const,
    assignmentPage: (slug: TenantSlug | string, query: Record<string, unknown>) =>
      [slug, "shift-assignments", query] as const,
  },
  /**
   * Phase 5 — attendance setup / policy (ATT-02, CC-16).
   */
  attendanceSetup: (slug: TenantSlug | string) => [slug, "attendance-setup"] as const,
  /** CC-571 — work locations (sites) and each employee's allowed list. */
  workLocations: {
    all: (slug: TenantSlug | string) => [slug, "work-locations"] as const,
    list: (slug: TenantSlug | string) => [slug, "work-locations", "list"] as const,
    homeSite: (slug: TenantSlug | string, employeeId: string) =>
      [slug, "work-locations", "home-site", employeeId] as const,
    employee: (slug: TenantSlug | string, employeeId: string) =>
      [slug, "work-locations", "employee", employeeId] as const,
  },
  /**
   * Phase 5 — notification bell + drawer (EW-15, CC-22).
   * The `list` key is reused by polling hooks with varying refetchInterval.
   */
  notifications: {
    list: (slug: TenantSlug | string) => [slug, "notifications", "list"] as const,
  },
  /**
   * Phase 5 — correction requests (ATT-05, EW-08, fe-032 assumed shape).
   * `list` takes an optional filter bag so per-status cache entries stay distinct.
   */
  correctionRequests: {
    list: (slug: TenantSlug | string, filters?: Record<string, unknown>) =>
      [slug, "correction-requests", "list", filters ?? {}] as const,
    detail: (slug: TenantSlug | string, id: string) =>
      [slug, "correction-requests", "detail", id] as const,
    /** CC-329 — presigned attachment download / availability probe. */
    attachment: (slug: TenantSlug | string, id: string) =>
      [slug, "correction-requests", "attachment", id] as const,
  },
  /**
   * Phase 05.1 — appraisal templates + cycles + reviews + export jobs
   * (PERF-01..PERF-08, CC-50). Mirrors `org` / `employees` shape:
   *   - `templates.{all,list,detail}`     — template catalog + editor (D-02)
   *   - `cycles.{all,list,detail,progress,reviews,peers}` — cycle wizard,
   *                                          progress dashboard (D-06/D-17),
   *                                          reviewer-matrix peer pre-assign
   *   - `reviews.{me,detail}`             — `me` is USER-scoped (JWT carries
   *                                          tenant per backend `/me/reviews`);
   *                                          `detail` is tenant-scoped
   *   - `exportJobs.job(slug, jobId)`     — async export polling key (D-15)
   *
   * `reviews.me` starts with the literal `"me"` sentinel (mirrors
   * `qk.me.preferences()` / `qk.me.employee()`) so logout flushes user-scoped
   * caches at the same prune root.
   */
  appraisal: {
    templates: {
      all: (slug: TenantSlug | string) => [slug, "appraisal", "templates"] as const,
      list: (slug: TenantSlug | string, query: Record<string, unknown>) =>
        [slug, "appraisal", "templates", "list", query] as const,
      detail: (slug: TenantSlug | string, id: string) =>
        [slug, "appraisal", "templates", "detail", id] as const,
    },
    cycles: {
      all: (slug: TenantSlug | string) => [slug, "appraisal", "cycles"] as const,
      list: (slug: TenantSlug | string, query: Record<string, unknown>) =>
        [slug, "appraisal", "cycles", "list", query] as const,
      detail: (slug: TenantSlug | string, id: string) =>
        [slug, "appraisal", "cycles", "detail", id] as const,
      /** Cycle progress dashboard polling root (D-17). */
      progress: (slug: TenantSlug | string, id: string) =>
        [slug, "appraisal", "cycles", "progress", id] as const,
      /** Per-cycle reviews list (filterable by kind/status). */
      reviews: (slug: TenantSlug | string, id: string, query: Record<string, unknown>) =>
        [slug, "appraisal", "cycles", "reviews", id, query] as const,
      /** Peer pre-assignments per cycle (DRAFT-only edits per backend). */
      peers: (slug: TenantSlug | string, id: string) =>
        [slug, "appraisal", "cycles", "peers", id] as const,
      /** HR calibration rating-distribution snapshot (CC-116, CLOSED-only). */
      ratingDistribution: (slug: TenantSlug | string, id: string, query: Record<string, unknown>) =>
        [slug, "appraisal", "cycles", "rating-distribution", id, query] as const,
      /** Population pre-flight count for a filter (`_resolve-count`, CC-475). */
      resolveCount: (slug: TenantSlug | string, query: Record<string, unknown>) =>
        [slug, "appraisal", "cycles", "resolve-count", query] as const,
      /** What `:activate` would create for a DRAFT (`activation-preview`, CC-476). */
      activationPreview: (slug: TenantSlug | string, id: string) =>
        [slug, "appraisal", "cycles", "activation-preview", id] as const,
      /**
       * Hub tile counts (`/appraisal/summary`). Under the `cycles` root on
       * purpose: every cycle and review mutation already invalidates that root.
       */
      summary: (slug: TenantSlug | string) => [slug, "appraisal", "cycles", "summary"] as const,
    },
    reviews: {
      /**
       * `/api/v1/appraisal/me/reviews` — USER-scoped (no tenant slug); JWT
       * carries the tenant per backend resolution. Mirrors `qk.me.preferences`.
       */
      me: () => ["me", "appraisal", "reviews"] as const,
      /** `/api/v1/appraisal/me/results` — USER-scoped results about the caller (CC-476). */
      meResults: () => ["me", "appraisal", "results"] as const,
      detail: (slug: TenantSlug | string, id: string) =>
        [slug, "appraisal", "reviews", "detail", id] as const,
    },
    exportJobs: {
      /** Async export job polling key (D-15). Wave 3 plan 11b populates. */
      job: (slug: TenantSlug | string, jobId: string) =>
        [slug, "appraisal", "export-jobs", "job", jobId] as const,
    },
  },
  /**
   * Phase 8 — payroll runs, payslips, configs (PAY-01..PAY-07, CC-37..CC-45).
   * Tenant-scoped sub-namespace mirroring qk.appraisal.* shape.
   * All keys start with [tenantSlug, "payroll", ...] for surgical invalidation
   * and logout-flush via qk.tenant(slug).
   */
  payroll: {
    runs: {
      all: (slug: TenantSlug | string) => [slug, "payroll", "runs"] as const,
      list: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
        [slug, "payroll", "runs", "list", query] as const,
      detail: (slug: TenantSlug | string, id: string) =>
        [slug, "payroll", "runs", "detail", id] as const,
      /** Persisted closing-room review marks (`GET|PUT {id}/review-marks`). */
      reviewMarks: (slug: TenantSlug | string, id: string) =>
        [slug, "payroll", "runs", "review-marks", id] as const,
      /**
       * Pre-flight gate (PAY-Preflight) — proactive `POST {id}:validate`
       * surfaced as a cached QUERY so the screen auto-runs the gate on
       * DRAFT/COMPUTED runs. Invalidated by compute/recall so the gate
       * re-runs after a recompute. The :validate POST is read-only (CC-79),
       * so caching its result is safe.
       */
      preflight: (slug: TenantSlug | string, runId: string) =>
        [slug, "payroll", "runs", runId, "preflight"] as const,
      /** A draft or calculated run's exceptions, one page of people per query. */
      exceptions: (slug: TenantSlug | string, runId: string, query: Record<string, unknown> = {}) =>
        [slug, "payroll", "runs", runId, "exceptions", query] as const,
      payslips: (slug: TenantSlug | string, runId: string, query: Record<string, unknown> = {}) =>
        [slug, "payroll", "runs", runId, "payslips", query] as const,
      /**
       * Closing Room — ALL payslip lines for a run, fetched by looping the
       * paginated endpoint until exhausted (server clamps size at 200). Keyed per
       * run id so the comparison query for current + prior runs cache independently.
       */
      payslipsAll: (slug: TenantSlug | string, runId: string) =>
        [slug, "payroll", "runs", runId, "payslips", "all"] as const,
      /**
       * CC-286 — single payslip-line fetch by id
       * (`GET /payroll/runs/{runId}/payslips/{lineId}`). Resolves the register
       * drill-in without scanning the paginated list (removes the 200-row ceiling).
       */
      payslipLine: (slug: TenantSlug | string, runId: string, lineId: string) =>
        [slug, "payroll", "runs", runId, "payslips", "line", lineId] as const,
      presign: (slug: TenantSlug | string, runId: string, lineId: string) =>
        [slug, "payroll", "runs", runId, "payslips", "presign", lineId] as const,
      /**
       * The lines the bank file cannot pay. Keyed with the same filters the
       * export sends, so the count the admin sees always describes the file
       * the export button would actually produce.
       */
      bankFileExclusions: (
        slug: TenantSlug | string,
        runId: string,
        filters: Record<string, unknown> = {},
      ) => [slug, "payroll", "runs", runId, "bank-file", "exclusions", filters] as const,
      /** The run's cash + CliQ payments. */
      disbursements: (slug: TenantSlug | string, runId: string) =>
        [slug, "payroll", "runs", runId, "disbursements"] as const,
      /** A finalized or paid run's payments by status, one page of them per query. */
      paymentReconciliation: (
        slug: TenantSlug | string,
        runId: string,
        query: Record<string, unknown> = {},
      ) => [slug, "payroll", "runs", runId, "payment-reconciliation", query] as const,
    },
    configs: {
      resource: (slug: TenantSlug | string, resource: string) =>
        [slug, "payroll", "configs", resource] as const,
      list: (slug: TenantSlug | string, resource: string, query: Record<string, unknown> = {}) =>
        [slug, "payroll", "configs", resource, "list", query] as const,
    },
    contributionRates: (slug: TenantSlug | string) =>
      [slug, "payroll", "contribution-rates"] as const,
    taxBracketsSchedule: (slug: TenantSlug | string, effectiveFrom: string) =>
      [slug, "payroll", "tax-brackets", "schedule", effectiveFrom] as const,
    /** Brackets IN EFFECT as of a date (or today). Distinct from the schedule
     *  key above, which addresses one specific published schedule by its start. */
    taxBracketsAsOf: (slug: TenantSlug | string, asOf: string | null) =>
      [slug, "payroll", "tax-brackets", "as-of", asOf] as const,
    eos: {
      detail: (slug: TenantSlug | string, employeeId: string) =>
        [slug, "payroll", "eos", employeeId] as const,
      /**
       * Phase 08 Plan 08-13 (PAY-06) — EOS calculation read.
       * Same shape as `detail` but explicit name aligns with the hook surface.
       * CC-300: optional `asOf` (as-of last working day) gets its own cache
       * entry so a Terminate-drawer date-pick refetches; omitting it keeps the
       * historical `"today"` key. The `[slug,"payroll","eos","calculation",id]`
       * prefix still matches every `asOf` variant for invalidation.
       */
      calculation: (slug: TenantSlug | string, employeeId: string, asOf?: string) =>
        [slug, "payroll", "eos", "calculation", employeeId, asOf ?? "today"] as const,
      /**
       * Phase 08 Plan 08-13 (PAY-06) — EOS audit-log entries (D-15).
       * Backed by `/api/v1/audit-log?employeeId&actions=EOS_OVERRIDDEN,...`.
       */
      auditLog: (slug: TenantSlug | string, employeeId: string) =>
        [slug, "payroll", "eos", "audit-log", employeeId] as const,
      /**
       * CC-118 (be-171) — tenant-wide EOS rollup. `asOf` defaults to today
       * server-side; we key on whatever the caller passed so a date-pick
       * gets its own cache entry.
       */
      tenantRollup: (slug: TenantSlug | string, asOf?: string) =>
        [slug, "payroll", "eos", "tenant-rollup", asOf ?? "today"] as const,
    },
    /**
     * CC-118 (be-171) — tenant-wide Financial Transactions read-only ledger.
     * Aggregates allowance + deduction + one_time_payment via UNION ALL
     * server-side; `id` is composite (`"<source>:<uuid>"`).
     */
    financialTransactions: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "payroll", "financial-transactions", query] as const,
    /** CC-299 — status-count summary for the FT ledger stat tiles. */
    financialTransactionSummary: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "payroll", "financial-transactions", "summary", query] as const,
    /** Prefix of the ledger, its summary and every single-row read: what a write invalidates. */
    financialTransactionsAll: (slug: TenantSlug | string) =>
      [slug, "payroll", "financial-transactions"] as const,
    financialTransaction: (slug: TenantSlug | string, id: string) =>
      [slug, "payroll", "financial-transactions", "detail", id] as const,
    /** The company's transaction names; the prefix without `includeInactive` is the write handle. */
    financialTransactionNames: (slug: TenantSlug | string, includeInactive?: boolean) =>
      includeInactive === undefined
        ? ([slug, "payroll", "financial-transaction-names"] as const)
        : ([slug, "payroll", "financial-transaction-names", includeInactive] as const),
    /** Per-employee one-time payments (bonus / reimbursement / other). */
    oneTimePayments: (slug: TenantSlug | string, employeeId: string) =>
      [slug, "payroll", "one-time-payments", employeeId] as const,
    /** Prefix of every employee's one-time payments. */
    oneTimePaymentsAll: (slug: TenantSlug | string) =>
      [slug, "payroll", "one-time-payments"] as const,
    /** Monthly payroll input imports — `all` is the handle after apply / revert. */
    inputImports: {
      all: (slug: TenantSlug | string) => [slug, "payroll", "input-imports"] as const,
      list: (slug: TenantSlug | string, year: number, month: number) =>
        [slug, "payroll", "input-imports", "list", year, month] as const,
      detail: (slug: TenantSlug | string, id: string) =>
        [slug, "payroll", "input-imports", "detail", id] as const,
    },
  },
  /**
   * Phase 8 — employee document requests (CC-40, D-11/D-12).
   * Tenant-scoped (first key = tenantSlug) for logout-flush and surgical
   * invalidation. `all` is the bulk-invalidation handle post-create.
   */
  documentRequests: {
    all: (slug: TenantSlug | string) => [slug, "document-requests"] as const,
    list: (slug: TenantSlug | string, query: Record<string, unknown> = {}) =>
      [slug, "document-requests", "list", query] as const,
    detail: (slug: TenantSlug | string, id: string) =>
      [slug, "document-requests", "detail", id] as const,
  },
  /**
   * Phase 9 — HR + Me dashboards (DASH-01 + DASH-03), report jobs + trend
   * direct-reads (DASH-04 / RPT-01..06), tenant announcements (CC-86).
   *
   * Tenant-scoped per FOUND-03; query/list params live in the tail so per-
   * filter cache entries stay distinct. The `dashboard.attendanceTrend`
   * branch is a sibling of the Phase 5 `attendance.*` keys but lives under
   * `dashboard` because it backs an HR Admin dashboard widget, not the
   * attendance feature surface.
   */
  dashboard: {
    hr: (slug: TenantSlug | string) => [slug, "dashboard", "hr"] as const,
    me: (slug: TenantSlug | string) => [slug, "dashboard", "me"] as const,
    attendanceTrend: (slug: TenantSlug | string, period: string, branchId?: string) =>
      [slug, "dashboard", "hr", "attendance-trend", period, branchId ?? null] as const,
    /** CC-149 Phase 28 — cross-role My Day rollup. */
    meDay: (slug: TenantSlug | string) => [slug, "dashboard", "me-day"] as const,
    /** The metrics this caller can put on a dashboard (`GET /dashboard/metrics`). */
    metrics: (slug: TenantSlug | string) => [slug, "dashboard", "metrics"] as const,
    /** One metric read; the params (period, groupBy, filters) key each variant. */
    metric: (slug: TenantSlug | string, metric: string, params: Record<string, unknown>) =>
      [slug, "dashboard", "metrics", metric, params] as const,
  },
  search: {
    /** CC-149 Phase 28 — global Cmd+K search results, keyed by (q, types). */
    results: (slug: TenantSlug | string, q: string, types: readonly string[] | null) =>
      [slug, "search", q, types ?? "all"] as const,
  },
  mePermissions: {
    /** CC-149 Phase 28 — `/me/permissions` { permissions, flags }. */
    detail: (slug: TenantSlug | string) => [slug, "me", "permissions"] as const,
  },
  accounting: {
    /** One company's people and their access — the prefix every access change invalidates. */
    people: (slug: TenantSlug | string, companyId: string) =>
      [slug, "accounting", "companies", companyId, "people"] as const,
    /** One page of that list, for one search. */
    peoplePage: (slug: TenantSlug | string, companyId: string, q: string, page: number) =>
      [slug, "accounting", "companies", companyId, "people", q, page] as const,
    /** The roles an owner can give (the same for every company). */
    grantTemplates: (slug: TenantSlug | string) => [slug, "accounting", "grant-templates"] as const,
    /** Every read of one company's books — the prefix a posting, set-up or settings change invalidates. */
    ledger: (slug: TenantSlug | string, companyId: string) =>
      [slug, "accounting", "companies", companyId, "ledger"] as const,
    /** One ledger read: the resource and its query. */
    ledgerRead: (
      slug: TenantSlug | string,
      companyId: string,
      resource: string,
      params: Readonly<Record<string, unknown>> = {},
    ) => [slug, "accounting", "companies", companyId, "ledger", resource, params] as const,
  },
  messages: {
    /** Invalidation root — send/mark-all-read invalidate this prefix. */
    all: (slug: TenantSlug | string) => [slug, "messages"] as const,
    /** Conversations sidebar list. */
    conversations: (slug: TenantSlug | string, limit: number) =>
      [slug, "messages", "conversations", limit] as const,
    /** Every page and size of the conversations list (a 1:1 edit or delete refreshes it). */
    conversationLists: (slug: TenantSlug | string) => [slug, "messages", "conversations"] as const,
    /** Who reacted to a 1:1 message. Outside the `messages` prefix, like the thread. */
    reactions: (slug: TenantSlug | string, messageId: string) =>
      [slug, "message-reactions", messageId] as const,
    /** One attachment's short-lived download link. Keyed OUTSIDE the `messages`
     *  prefix so the broad send / delivered / read invalidations (which target
     *  `messages.all`) don't mint every link again. */
    attachmentUrl: (slug: TenantSlug | string, messageId: string, attachmentId: string) =>
      [slug, "message-attachment-url", messageId, attachmentId] as const,
    /** Every loaded 1:1 thread. Keyed OUTSIDE the `messages` prefix, so the
     *  delivered / read / send invalidations there don't read every loaded page
     *  again: a thread is refetched for its peer's new message only, and ticks
     *  come from its newest page. */
    directThreads: (slug: TenantSlug | string) => [slug, "dm-thread"] as const,
    /** One peer's thread; the hook appends the page size. */
    directThread: (slug: TenantSlug | string, peerUserId: string) =>
      [slug, "dm-thread", peerUserId] as const,
  },
  /**
   * Who the caller may START a 1:1 with. Outside the `messages` prefix so a send
   * doesn't refetch it; the ids are sorted into the key so the same set (in any
   * order) reads one cache entry.
   */
  messageEligibility: {
    all: (slug: TenantSlug | string) => [slug, "message-eligibility"] as const,
    ids: (slug: TenantSlug | string, userIds: readonly string[]) =>
      [slug, "message-eligibility", [...userIds].sort().join(",")] as const,
  },
  /**
   * Chat groups. Kept OUTSIDE the `messages` prefix: every direct-message send
   * invalidates `messages.all`, and that must not refetch the group screens.
   */
  messageGroups: {
    all: (slug: TenantSlug | string) => [slug, "message-groups"] as const,
    /** HR's group management screens. */
    admin: {
      all: (slug: TenantSlug | string) => [slug, "message-groups", "admin"] as const,
      list: (slug: TenantSlug | string) => [slug, "message-groups", "admin", "list"] as const,
      /** Prefix of the group's members too, so one invalidation refreshes both. */
      detail: (slug: TenantSlug | string, groupId: string) =>
        [slug, "message-groups", "admin", "detail", groupId] as const,
      members: (slug: TenantSlug | string, groupId: string) =>
        [slug, "message-groups", "admin", "detail", groupId, "members"] as const,
    },
    /** The caller's own groups in the chat. */
    chat: {
      all: (slug: TenantSlug | string) => [slug, "message-groups", "chat"] as const,
      /** The rail list; hooks append the page size. */
      list: (slug: TenantSlug | string) => [slug, "message-groups", "chat", "list"] as const,
      /** One group's messages; hooks append the page size. */
      thread: (slug: TenantSlug | string, groupId: string) =>
        [slug, "message-groups", "chat", "thread", groupId] as const,
      /** One group's members, as a member sees them. */
      members: (slug: TenantSlug | string, groupId: string) =>
        [slug, "message-groups", "chat", "members", groupId] as const,
      /** Who reacted to one group message. */
      reactions: (slug: TenantSlug | string, groupId: string, messageId: string) =>
        [slug, "message-groups", "chat", "reactions", groupId, messageId] as const,
      /** Who an own group message reached and who saw it; without a message, the group's. */
      receipts: (slug: TenantSlug | string, groupId: string, messageId?: string) =>
        messageId === undefined
          ? ([slug, "message-groups", "chat", "receipts", groupId] as const)
          : ([slug, "message-groups", "chat", "receipts", groupId, messageId] as const),
    },
  },
  reports: {
    /** Tenant-scoped invalidation root; mutations use this on success. */
    all: (slug: TenantSlug | string) => [slug, "reports"] as const,
    jobs: (slug: TenantSlug | string, params: Record<string, unknown> = {}) =>
      [slug, "reports", "jobs", params] as const,
    job: (slug: TenantSlug | string, jobId: string) =>
      [slug, "reports", "jobs", "detail", jobId] as const,
    /** Per-status job counts, narrowed by report type like the list. */
    jobsSummary: (slug: TenantSlug | string, reportType: string | null) =>
      [slug, "reports", "jobs", "summary", reportType] as const,
    trend: (
      slug: TenantSlug | string,
      kind: "headcount" | "payrollCost" | "turnover",
      months: number,
    ) => [slug, "reports", "trend", kind, months] as const,
    /**
     * Builder v2 catalog. Caller-scoped AND narrowed by `rootEntity`, so the
     * root is part of the key — two roots are two different catalogs, not two
     * views of one.
     */
    builderV2Catalog: (slug: TenantSlug | string, rootEntity: string | null) =>
      [slug, "reports", "builder", "v2", "catalog", rootEntity] as const,
    /** Daily export-quota window per (tenant, reportType) — read-only, no slot consumed. */
    quota: (slug: TenantSlug | string, reportType: string) =>
      [slug, "reports", "quota", reportType] as const,
    /** Server-driven template report catalog (types + param specs). */
    catalog: (slug: TenantSlug | string) => [slug, "reports", "catalog"] as const,
  },
  savedReports: {
    /** Invalidation root for saved-report mutations. */
    all: (slug: TenantSlug | string) => [slug, "saved-reports"] as const,
    list: (slug: TenantSlug | string, params: Record<string, unknown> = {}) =>
      [slug, "saved-reports", "list", params] as const,
    detail: (slug: TenantSlug | string, id: string) =>
      [slug, "saved-reports", "detail", id] as const,
    /**
     * Schedules nested under a saved report. `params` (page/size) is appended
     * LAST so the existing mutation invalidations, which pass only the saved
     * report id, still prefix-match every page of that report's schedules.
     */
    schedules: (
      slug: TenantSlug | string,
      savedReportId: string,
      params?: Record<string, unknown>,
    ): readonly unknown[] =>
      params === undefined
        ? [slug, "saved-reports", "detail", savedReportId, "schedules"]
        : [slug, "saved-reports", "detail", savedReportId, "schedules", params],
  },
  scheduledReports: {
    /** Invalidation root for schedule mutations (PATCH / DELETE / :fire-now). */
    all: (slug: TenantSlug | string) => [slug, "scheduled-reports"] as const,
    detail: (slug: TenantSlug | string, scheduleId: string) =>
      [slug, "scheduled-reports", "detail", scheduleId] as const,
  },
  announcements: {
    all: (slug: TenantSlug | string) => [slug, "announcements"] as const,
    list: (slug: TenantSlug | string, params: Record<string, unknown> = {}) =>
      [slug, "announcements", "list", params] as const,
    detail: (slug: TenantSlug | string, id: string) =>
      [slug, "announcements", "detail", id] as const,
  },
  /**
   * Phase 16 — configurable approval chains (APPR-01, be-124 / CC-105 pending).
   *
   * Tenant-scoped per FOUND-03. `list` accepts optional status / requestType
   * filters — they live in the tail so per-filter cache entries stay distinct.
   * The matrix LIST is its own root because the response is a flat list of
   * (requestType, chainId) rows independent of any single chain id.
   *
   *   approvalChains.list(slug, { status: "ACTIVE" })
   *     → [slug, "approval-chains", "list", { status: "ACTIVE" }]
   *   approvalChains.assignments(slug, chainId)
   *     → [slug, "approval-chains", "assignments", chainId]
   *   approvalChains.matrix(slug)
   *     → [slug, "approval-chains", "matrix"]
   *
   * Preview is mutation-style (on-demand, not cached) but exposes a key so
   * future "save the latest preview for this builder session" optimization
   * has a stable invalidation root.
   */
  approvalChains: {
    all: (slug: TenantSlug | string) => [slug, "approval-chains"] as const,
    list: (slug: TenantSlug | string, filters: Record<string, unknown> = {}) =>
      [slug, "approval-chains", "list", filters] as const,
    detail: (slug: TenantSlug | string, id: string) =>
      [slug, "approval-chains", "detail", id] as const,
    assignments: (slug: TenantSlug | string, chainId: string) =>
      [slug, "approval-chains", "assignments", chainId] as const,
    matrix: (slug: TenantSlug | string) => [slug, "approval-chains", "matrix"] as const,
    requestTypes: (slug: TenantSlug | string) =>
      [slug, "approval-chains", "request-types"] as const,
    preview: (slug: TenantSlug | string, id: string) =>
      [slug, "approval-chains", "preview", id] as const,
  },
  /**
   * Phase 17 — loans domain (LOAN-01..04, be-130 wire shapes; CC-106 pending).
   *
   * Tenant-scoped per FOUND-03. Two top-level namespaces share the cache root:
   *   - `loanTypes.{all,list,detail}`         — HR-Admin catalog CRUD
   *   - `loans.{all,list,detail,schedule}`    — HR-Admin roster + schedule
   *   - `meLoans.{aggregate,schedule}`        — Employee Web /me aggregator
   *
   * `meLoans.*` is tenant-scoped (not user-scoped) because the same caller
   * may switch tenants in dev (impersonation flips); keying off slug keeps
   * cache boundaries clean and parallel to the rest of the qk factory.
   */
  loanTypes: {
    all: (slug: TenantSlug | string) => [slug, "loan-types"] as const,
    list: (slug: TenantSlug | string, filters: Record<string, unknown> = {}) =>
      [slug, "loan-types", "list", filters] as const,
    detail: (slug: TenantSlug | string, id: string) => [slug, "loan-types", "detail", id] as const,
  },
  loans: {
    all: (slug: TenantSlug | string) => [slug, "loans"] as const,
    list: (slug: TenantSlug | string, filters: Record<string, unknown> = {}) =>
      [slug, "loans", "list", filters] as const,
    detail: (slug: TenantSlug | string, id: string) => [slug, "loans", "detail", id] as const,
    schedule: (slug: TenantSlug | string, id: string) => [slug, "loans", "schedule", id] as const,
  },
  meLoans: {
    aggregate: (slug: TenantSlug | string) => [slug, "me-loans", "aggregate"] as const,
    schedule: (slug: TenantSlug | string, id: string) =>
      [slug, "me-loans", "schedule", id] as const,
  },
  /**
   * Phase 18 — assets domain (AST-01..03, be-133 wire shapes; CC-107 pending).
   *
   * Tenant-scoped per FOUND-03. Three top-level namespaces share the cache root:
   *   - `assetCategories.{all,list,detail}`     — HR-Admin catalog CRUD
   *   - `assets.{all,list,detail}`              — HR-Admin roster + detail
   *   - `meAssets.aggregate(slug)`              — Employee Web /me aggregator
   *   - `unreturnedAssets.byEmployee(slug,id)`  — Phase 20 Lifecycle consumer
   *
   * `meAssets.*` is tenant-scoped (not user-scoped) because the same caller
   * may switch tenants in dev (impersonation flips); keying off slug keeps
   * cache boundaries clean and parallel to the rest of the qk factory.
   */
  assetCategories: {
    all: (slug: TenantSlug | string) => [slug, "asset-categories"] as const,
    list: (slug: TenantSlug | string, filters: Record<string, unknown> = {}) =>
      [slug, "asset-categories", "list", filters] as const,
    detail: (slug: TenantSlug | string, id: string) =>
      [slug, "asset-categories", "detail", id] as const,
  },
  assets: {
    all: (slug: TenantSlug | string) => [slug, "assets"] as const,
    list: (slug: TenantSlug | string, filters: Record<string, unknown> = {}) =>
      [slug, "assets", "list", filters] as const,
    detail: (slug: TenantSlug | string, id: string) => [slug, "assets", "detail", id] as const,
    /**
     * Phase 18 Plan 02 — full allocation history per asset
     * (`GET /api/v1/assets/{id}/allocations`). Sub-key under the asset's
     * detail root so a refetch of the parent asset cleanly invalidates
     * downstream allocation lists too.
     */
    allocations: (slug: TenantSlug | string, assetId: string) =>
      [slug, "assets", "allocations", assetId] as const,
    /**
     * Phase 18 Plan 02 — maintenance log per asset
     * (`GET /api/v1/assets/{id}/maintenance`). Same shape rationale as
     * `allocations` above.
     */
    maintenance: (slug: TenantSlug | string, assetId: string) =>
      [slug, "assets", "maintenance", assetId] as const,
  },
  meAssets: {
    aggregate: (slug: TenantSlug | string) => [slug, "me-assets", "aggregate"] as const,
  },
  unreturnedAssets: {
    byEmployee: (slug: TenantSlug | string, employeeId: string) =>
      [slug, "unreturned-assets", "by-employee", employeeId] as const,
  },
  /**
   * Phase 19 — expenses domain (EXP-01..03 + travel/trips, be-137 wire shapes;
   * CC-108 pending).
   *
   * Tenant-scoped per FOUND-03. Three top-level namespaces share the cache root:
   *   - `expenseCategories.{all,list,detail}`     — HR-Admin catalog CRUD
   *   - `expenseReports.{all,list,detail}`        — HR-Admin roster + detail
   *   - `meExpenseReports.{aggregate,detail}`     — Employee Web /me aggregator
   *
   * Travel + trip namespaces ship in 19-02 alongside the screens that consume
   * them. `meExpenseReports.*` is tenant-scoped (not user-scoped) so cache
   * boundaries stay parallel to the rest of the qk factory.
   */
  expenseCategories: {
    all: (slug: TenantSlug | string) => [slug, "expense-categories"] as const,
    list: (slug: TenantSlug | string, filters: Record<string, unknown> = {}) =>
      [slug, "expense-categories", "list", filters] as const,
    detail: (slug: TenantSlug | string, id: string) =>
      [slug, "expense-categories", "detail", id] as const,
  },
  expenseReports: {
    all: (slug: TenantSlug | string) => [slug, "expense-reports"] as const,
    list: (slug: TenantSlug | string, filters: Record<string, unknown> = {}) =>
      [slug, "expense-reports", "list", filters] as const,
    detail: (slug: TenantSlug | string, id: string) =>
      [slug, "expense-reports", "detail", id] as const,
  },
  meExpenseReports: {
    aggregate: (slug: TenantSlug | string) => [slug, "me-expense-reports", "aggregate"] as const,
    detail: (slug: TenantSlug | string, id: string) =>
      [slug, "me-expense-reports", "detail", id] as const,
  },
  /**
   * Phase 19 Plan 02 — travel-requests + business-trips (be-137).
   *
   * Tenant-scoped per FOUND-03. Three top-level namespaces:
   *   - `travelRequests.{all,list,detail}`   — HR-Admin roster + detail
   *   - `meTravelRequests.{aggregate,detail}` — Employee Web /me aggregator
   *   - `businessTrips.{all,list}`           — HR-Admin trip roster
   */
  travelRequests: {
    all: (slug: TenantSlug | string) => [slug, "travel-requests"] as const,
    list: (slug: TenantSlug | string, filters: Record<string, unknown> = {}) =>
      [slug, "travel-requests", "list", filters] as const,
    detail: (slug: TenantSlug | string, id: string) =>
      [slug, "travel-requests", "detail", id] as const,
    /** CC-301 — supported travel-cost currency catalog ({code, nameEn, nameAr}). */
    currencies: (slug: TenantSlug | string) => [slug, "travel-requests", "currencies"] as const,
  },
  meTravelRequests: {
    aggregate: (slug: TenantSlug | string) => [slug, "me-travel-requests", "aggregate"] as const,
    detail: (slug: TenantSlug | string, id: string) =>
      [slug, "me-travel-requests", "detail", id] as const,
  },
  businessTrips: {
    all: (slug: TenantSlug | string) => [slug, "business-trips"] as const,
    list: (slug: TenantSlug | string, filters: Record<string, unknown> = {}) =>
      [slug, "business-trips", "list", filters] as const,
  },
  /**
   * Phase 20 — lifecycle domain (BOARD-01..06, be-145 + be-146 + be-148 wire
   * shapes; CC-111 + CC-112 + CC-113 shipped).
   *
   * Tenant-scoped per FOUND-03. Four top-level namespaces share the cache root:
   *   - `lifecycleTemplates.{all,list,detail}`   — HR-Admin template CRUD
   *   - `lifecycleRuns.{all,list,detail}`        — HR-Admin run roster + detail
   *   - `lifecyclePreview.byTemplate(slug,id,e)` — Template preview-as-employee
   *   - `meLifecycleRuns.{list,detail}`          — Employee Web /me aggregator
   */
  lifecycleTemplates: {
    all: (slug: TenantSlug | string) => [slug, "lifecycle-templates"] as const,
    list: (slug: TenantSlug | string, filters: Record<string, unknown> = {}) =>
      [slug, "lifecycle-templates", "list", filters] as const,
    detail: (slug: TenantSlug | string, id: string) =>
      [slug, "lifecycle-templates", "detail", id] as const,
  },
  lifecycleRuns: {
    all: (slug: TenantSlug | string) => [slug, "lifecycle-runs"] as const,
    list: (slug: TenantSlug | string, filters: Record<string, unknown> = {}) =>
      [slug, "lifecycle-runs", "list", filters] as const,
    detail: (slug: TenantSlug | string, id: string) =>
      [slug, "lifecycle-runs", "detail", id] as const,
  },
  lifecyclePreview: {
    byTemplate: (slug: TenantSlug | string, templateId: string, employeeId: string) =>
      [slug, "lifecycle-preview", templateId, employeeId] as const,
  },
  meLifecycleRuns: {
    all: (slug: TenantSlug | string) => [slug, "me-lifecycle-runs"] as const,
    list: (slug: TenantSlug | string) => [slug, "me-lifecycle-runs", "list"] as const,
    detail: (slug: TenantSlug | string, id: string) =>
      [slug, "me-lifecycle-runs", "detail", id] as const,
  },
  /**
   * Phase 28.1 — /me/dashboard 10-field aggregator (CC-114) + alert dismissal
   * (CC-115). Single-key namespace; no params on the endpoint.
   *   - `aggregate(slug)` keys the dashboard payload (greeting, todayShift,
   *     leaveBalance, alerts, upcomingHolidays, announcements,
   *     quickActionsConfig, upcomingTasks, requestsStatusCount).
   * Tenant-scoped despite being driven server-side from the JWT `sub` —
   * matches the broader qk shape so `removeQueries({ queryKey: qk.tenant(slug) })`
   * flushes the dashboard on tenant flip.
   */
  meDashboard: {
    all: (slug: TenantSlug | string) => [slug, "me-dashboard"] as const,
    aggregate: (slug: TenantSlug | string) => [slug, "me-dashboard", "aggregate"] as const,
  },
  /**
   * Caller-scoped self-service reads the JWT resolves server-side: colleague
   * birthdays, this cycle's projected payslip and the caller's own activity
   * log. Tenant-prefixed so a tenant flip flushes them.
   */
  meSelf: {
    all: (slug: TenantSlug | string) => [slug, "me-self"] as const,
    birthdays: (slug: TenantSlug | string, params: { from: string; to: string }) =>
      [slug, "me-self", "birthdays", params] as const,
    currentCyclePayslip: (slug: TenantSlug | string) =>
      [slug, "me-self", "current-cycle-payslip"] as const,
    auditLog: (slug: TenantSlug | string, params: { page: number; size: number }) =>
      [slug, "me-self", "audit-log", params] as const,
  },
  /**
   * /me/shifts — caller's effective + upcoming shift assignments. Optional
   * `from` / `to` query params widen or narrow the resolved-week window.
   */
  meShifts: {
    all: (slug: TenantSlug | string) => [slug, "me-shifts"] as const,
    list: (
      slug: TenantSlug | string,
      params: { from?: string | undefined; to?: string | undefined },
    ) => [slug, "me-shifts", "list", params] as const,
    /**
     * CC-287 — server-resolved effective daily schedule
     * (`GET /me/shifts/effective?from=&to=`). Grid + Ramadan + holiday/weekend
     * already folded in per day.
     */
    effective: (
      slug: TenantSlug | string,
      params: { from?: string | undefined; to?: string | undefined },
    ) => [slug, "me-shifts", "effective", params] as const,
  },
  /**
   * Platform-scoped (Link Admin / `aud=platform`) cache root. All keys start
   * with `["_platform", ...]` so a single `removeQueries({ queryKey:
   * ["_platform"] })` flushes the entire platform surface on logout.
   *
   * Phase 6 (2026-04) added five sub-namespaces alongside the existing
   * `tenants` / `auditLog` / `session` entries to cover PLATBILL-01..06 +
   * LADM-05/08 surfaces:
   *   - `plans`           — subscription plan catalog CRUD
   *   - `subscriptions`   — per-tenant subscription read/PATCH
   *   - `billingHistory`  — per-tenant closed-period ledger
   *   - `mrr`             — cross-tenant monthly recurring revenue rollup
   *   - `adminInvites`    — per-tenant first-admin invite resend handle
   */
  /**
   * Phase 26 / CC-142-PREVIEW (be-207) — OT-types catalog (PAY2-04).
   * Tenant-scoped per FOUND-03. `all` is the bulk-invalidation handle.
   */
  overtimeTypes: {
    all: (slug: TenantSlug | string) => [slug, "overtime-types"] as const,
    list: (slug: TenantSlug | string, opts: { includeArchived?: boolean } = {}) =>
      [slug, "overtime-types", "list", opts] as const,
    /** The employee's own readable kinds — scoped to the caller, not the tenant. */
    mine: (slug: TenantSlug | string) => [slug, "overtime-types", "mine"] as const,
  },
  /**
   * Phase 26 / CC-142-PREVIEW (be-207) — Cost Center catalog (CC-FK-01).
   * Tenant-scoped per FOUND-03. No auto-seed (opt-in per-tenant catalog).
   */
  costCenters: {
    all: (slug: TenantSlug | string) => [slug, "cost-centers"] as const,
    list: (slug: TenantSlug | string, opts: { includeArchived?: boolean } = {}) =>
      [slug, "cost-centers", "list", opts] as const,
  },
  /**
   * Phase 26 / CC-142-PREVIEW (be-207) — per-employee primary cost-center FK (CC-FK-02).
   * GET returns CostCenterView or 204 if no assignment.
   * PUT with costCenterId=null clears the assignment.
   */
  employeeCostCenter: (slug: TenantSlug | string, employeeId: string) =>
    [slug, "employee-cost-center", employeeId] as const,
  /**
   * Phase 26 / CC-142-PREVIEW (be-207) — part-time rules per employee (PAY2-02).
   * Effective-dated (PATCH closes current row + opens new). Pro-gated.
   */
  partTimeRules: {
    all: (slug: TenantSlug | string, employeeId: string) =>
      [slug, "part-time-rules", employeeId] as const,
    list: (slug: TenantSlug | string, employeeId: string) =>
      [slug, "part-time-rules", employeeId, "list"] as const,
  },
  /**
   * Phase 26 / CC-142-PREVIEW (be-207) — insurance contributions per employee (PAY2-03).
   * Per-side XOR (pct OR amount). Effective-dated. Pro-gated.
   */
  insuranceContributions: {
    all: (slug: TenantSlug | string, employeeId: string) =>
      [slug, "insurance-contributions", employeeId] as const,
    list: (slug: TenantSlug | string, employeeId: string) =>
      [slug, "insurance-contributions", employeeId, "list"] as const,
  },
  /**
   * The tenant's own GOSI registrations. Tenant-wide, no employee scope —
   * `GET /api/v1/tenants/me/gosi-establishments`.
   */
  gosiEstablishments: {
    all: (slug: TenantSlug | string) => [slug, "gosi-establishments"] as const,
    list: (slug: TenantSlug | string) => [slug, "gosi-establishments", "list"] as const,
  },
  /**
   * The adopted statutory release resolved for today — contribution rates, tax
   * regime, EOS and sick-pay ladders. `GET /tenants/me/statutory-rates`.
   * Read-only; changes only when the platform adopts a release.
   */
  statutoryRates: (slug: TenantSlug | string) => [slug, "statutory-rates"] as const,
  /**
   * Per-employee social-insurance enrollments (GOSI / SSC). Effective-dated,
   * verified one-way. `GET /api/v1/employees/{id}/insurance-enrollments`.
   */
  insuranceEnrollments: {
    all: (slug: TenantSlug | string, employeeId: string) =>
      [slug, "insurance-enrollments", employeeId] as const,
    list: (slug: TenantSlug | string, employeeId: string) =>
      [slug, "insurance-enrollments", employeeId, "list"] as const,
    /** Tenant-wide listing — every employee's enrollment in one read. */
    tenantList: (slug: TenantSlug | string) =>
      [slug, "insurance-enrollments", "tenant", "list"] as const,
    tenantAll: (slug: TenantSlug | string) => [slug, "insurance-enrollments", "tenant"] as const,
    /** Server-computed per-employee payroll readiness for the whole tenant. */
    readiness: (slug: TenantSlug | string) =>
      [slug, "insurance-enrollments", "tenant", "readiness"] as const,
    /**
     * GOSI contributor reconciliation — the uploaded workbook's comparison job
     * and its per-employee rows. `GET /tenants/me/insurance-enrollments/
     * reconciliation/{jobId}` (+ `/rows`).
     */
    reconciliation: {
      all: (slug: TenantSlug | string) =>
        [slug, "insurance-enrollments", "reconciliation"] as const,
      /** Open/partially-applied reviews — the resume affordance's list. */
      openJobs: (slug: TenantSlug | string) =>
        [slug, "insurance-enrollments", "reconciliation", "open-jobs"] as const,
      job: (slug: TenantSlug | string, jobId: string) =>
        [slug, "insurance-enrollments", "reconciliation", jobId] as const,
      rows: (slug: TenantSlug | string, jobId: string) =>
        [slug, "insurance-enrollments", "reconciliation", jobId, "rows"] as const,
    },
  },
  /**
   * Phase 26 / CC-142-PREVIEW (be-207) — taxable-component config (PAY2-05).
   * Tenant-wide singleton; effective-dated PUT closes current + opens new.
   */
  taxableComponentConfig: (slug: TenantSlug | string) =>
    [slug, "taxable-component-config"] as const,
  /**
   * Phase 26 / CC-142-PREVIEW (be-207) — EOS Liability dashboard (RPT2-03, new endpoint).
   * GET /api/v1/dashboards/eos-liability — DIFFERENT from CC-118 /payroll/eos-liability.
   * New endpoint adds tenantTotal + top10HighestLiability + trend12mo aggregate.
   */
  eosLiabilityDashboard: (slug: TenantSlug | string) => [slug, "eos-liability-dashboard"] as const,
  platform: {
    tenants: {
      all: () => ["_platform", "tenants"] as const,
      list: (query: Record<string, unknown>) => ["_platform", "tenants", "list", query] as const,
      detail: (id: string) => ["_platform", "tenants", "detail", id] as const,
      branding: (id: string) => ["_platform", "tenants", "branding", id] as const,
      /**
       * The company identity one tenant's letters print under. Separate from
       * `detail` because the detail only carries the `configured` flag, and a
       * platform save has to invalidate both — the flag is what the tenant
       * page's Account row reads.
       */
      letterhead: (id: string) => ["_platform", "tenants", "letterhead", id] as const,
    },
    plans: {
      all: () => ["_platform", "plans"] as const,
      list: () => ["_platform", "plans", "list"] as const,
      detail: (code: string) => ["_platform", "plans", "detail", code] as const,
    },
    /**
     * CC-460 — the feature/limit catalog. One global list, revision-stamped
     * server-side, so it caches for an hour; editors refetch on open because a
     * stale key-space is what makes a wholesale featureSet write drop keys.
     */
    featureCatalog: () => ["_platform", "feature-catalog"] as const,
    subscriptions: {
      all: () => ["_platform", "subscriptions"] as const,
      detail: (tenantId: string) => ["_platform", "subscriptions", "detail", tenantId] as const,
      featureOverrides: (tenantId: string) =>
        ["_platform", "subscriptions", "feature-overrides", tenantId] as const,
    },
    assistant: {
      all: () => ["_platform", "assistant"] as const,
      /** Tenant board (CC-440) — paged, so the query object is part of the key. */
      usageBoard: (query: Record<string, unknown> = {}) =>
        ["_platform", "assistant", "usage-board", query] as const,
      /** One tenant's budget / caps / usage detail. */
      tenantUsage: (tenantId: string) =>
        ["_platform", "assistant", "tenant-usage", tenantId] as const,
      /** Plan-vs-override resolution for the tenant's features + limits. */
      resolvedOverrides: (tenantId: string) =>
        ["_platform", "assistant", "resolved-overrides", tenantId] as const,
    },
    billingHistory: {
      all: () => ["_platform", "billing-history"] as const,
      list: (tenantId: string, query: Record<string, unknown> = {}) =>
        ["_platform", "billing-history", "list", tenantId, query] as const,
    },
    mrr: () => ["_platform", "mrr"] as const,
    dashboard: (asOf?: string) => ["_platform", "dashboard", asOf ?? "now"] as const,
    invoices: {
      all: () => ["_platform", "invoices"] as const,
      list: (query: Record<string, unknown>) => ["_platform", "invoices", "list", query] as const,
      detail: (id: string) => ["_platform", "invoices", "detail", id] as const,
    },
    adminInvites: {
      all: (tenantId: string) => ["_platform", "admin-invites", tenantId] as const,
    },
    auditLog: (query: Record<string, unknown>) => ["_platform", "audit-log", query] as const,
    session: () => ["_platform", "session"] as const,
    /** The signed-in platform admin's own refresh-token families. */
    authSessions: () => ["_platform", "auth-sessions"] as const,
    /** Whether the signed-in platform admin has two-step sign-in on. */
    totpStatus: () => ["_platform", "totp-status"] as const,
    /**
     * Whether one tenant's payroll engine may run — the HR_ONLY → CERTIFYING →
     * READY state a platform admin moves. Not the tenant-facing
     * `/tenants/me/jurisdiction` view, which is a different audience.
     */
    payrollReadiness: (tenantId: string) => ["_platform", "payroll-readiness", tenantId] as const,
    /** The statutory-release catalog, filtered by jurisdiction + status. */
    statutoryReleases: (query: Record<string, unknown>) =>
      ["_platform", "statutory-releases", query] as const,
  },
} as const;
